/*
 * Copyright (c) 2024-2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, {useEffect, useState} from "react";
import {closeModal} from "helpers/actions";
import {useDispatch} from "useDispatch";
import {useTypedSelector} from "helpers/reducers";
import clsx from "clsx";
import {XMarkIcon} from "@heroicons/react/24/solid";

export enum ModalSize {
    Small = "small",
    Medium = "medium",
    FullHeight = "fullHeight",
    RoundedFullscreen = "roundedFullscreen",
    FullScreen = "fullscreen",
    Scrollable = "scrollable",
}

const Popup = () => {
    const popup = useTypedSelector(state => state.modal);
    const blur = useTypedSelector(state => state.blur);
    const [reload, setReload] = useState(performance.navigation.type);
    const isOpen = popup.isOpen || false;
    const isCloseEnabled = popup.props?.isCloseEnabled === undefined ? true : popup.props.isCloseEnabled;

    const [isLoading, setIsLoading] = useState(true);
    //const popup = null;
    const dispatch = useDispatch();
    const popupSize = popup.props?.modalSize || ModalSize.FullScreen;

    useEffect(() => {
        if (window.performance) {
            if (reload == 1) {
                dispatch(closeModal());
            }
        }
        const close = e => {
            if (e.keyCode === 27) {
                closePopup();
            }
        };
        if (isCloseEnabled) {
            window.addEventListener("keydown", close);
        }

        return () => window.removeEventListener("keydown", close);
    }, []);

    useEffect(() => {
        closePopup();
    }, [location.pathname]);

    const closePopup = () => {
        dispatch(closeModal());
    };

    useEffect(() => {
        if (popup.component) {
            setTimeout(() => {
                setIsLoading(false);
            }, 4);
        } else {
            setIsLoading(true);
        }
    }, [popup]);

    if (!popup.component) {
        return null;
    }

    if (!React.isValidElement(popup.component)) {
        return null;
    }

    const closeModalCallback = popup.props?.closeModal;

    const closeModalHandler = () => {
        return closeModalCallback ? closeModalCallback() : dispatch(closeModal());
    };

    return (
        <div
            className={clsx(
                "Popup flex overscroll-contain fixed  z-50 w-full h-full justify-center items-center transform-gpu bg-gray-800 bg-opacity-80",
                popupSize === ModalSize.FullScreen && "inset-0 w-screen h-screen min-h-[100vh] ",
                popupSize === ModalSize.FullHeight && "top-0 bottom-0 h-screen min-h-[100vh] w-auto py-2",
                popupSize === ModalSize.Small && "bg-dark-800 inset-0 min-h-[50vh] min-w-[50vw]",
                popupSize === ModalSize.Medium && "inset-0 min-h-[50vh] min-w-[50vw] overflow-hidden py-2",
                popupSize === ModalSize.Scrollable && "bg-dark-800 px-4 py-4 bottom-0 right-0 left-0 top-0 min-h-[50vh] min-w-[50vw] overflow-hidden",
            )}
            style={{
                transition: "300ms",
                transform: "translate3d(0px, 0px, 0px) scale3d(1, 1, 1) rotateX(0deg) rotateY(0deg) rotateZ(0deg) skew(0deg, 0deg)",
                transformStyle: "preserve-3d",
                opacity: isOpen && !isLoading ? 1 : 0,
                display: isOpen ? "flex" : "none",
            }}
        >
            <div
                className={clsx(
                    "BackdropInner fixed inset-0 flex  justify-center items-center z-40 ",
                    blur && popupSize === ModalSize.Small ? "backdrop-blur-sm" : "backdrop-opacity-50",
                    blur && popupSize === ModalSize.Medium ? "backdrop-blur-sm" : "backdrop-opacity-50",
                    blur && popupSize === ModalSize.Scrollable ? "backdrop-blur-sm" : "backdrop-opacity-50",
                )}
                id={"CloseFilterModal"}
                style={{filter: blur && "blur(2px)"}}
                onClick={isCloseEnabled ? closeModalHandler : () => false}
            />
            <div
                className={clsx(
                    "z-40 relative",
                    popupSize === ModalSize.RoundedFullscreen && "rounded-tl-4xl bg-white overflow-auto inner-shadow w-[calc(100vw-7rem)] h-[calc(100vh-5rem)]",
                    popupSize === ModalSize.Small && "h-auto bg-white overflow-auto m-10 max-w-screen-3xl rounded-lg live:max-w-screen-live",
                    popupSize === ModalSize.Medium &&
                        "overflow-auto m-10 max-w-screen-3xl rounded-lg live:max-w-screen-live h-full relative flex justify-center items-center",
                    popupSize === ModalSize.FullScreen && "w-full bg-white overflow-auto h-full flex flex-col justify-center items-center",
                    popupSize === ModalSize.FullHeight && "w-auto bg-white overflow-auto h-full",
                    popupSize === ModalSize.Scrollable && "flex flex-col h-auto max-w-screen-3xl rounded-lg live:max-w-screen-live",
                )}
            >
                {(isCloseEnabled && popupSize !== ModalSize.Scrollable) && (
                    // {(isCloseEnabled) &&
                    <button
                        className={clsx(
                            "print:hidden bg-red-500 text-white absolute right-0 top-0 z-[100]",
                            (popupSize === ModalSize.FullHeight || popupSize === ModalSize.Medium) && "h-12 p-3",
                            popupSize === ModalSize.FullScreen && "h-18 p-6",
                            popupSize === ModalSize.Small && "h-12 p-3"
                            // popupSize === ModalSize.Medium && "left-auto top-0 right-0 z-30 !sticky",
                        )}
                        onClick={closeModalHandler}
                    >
                        <XMarkIcon
                            className={clsx(
                                "text-white hover:opacity-70",
                                popupSize === ModalSize.FullHeight || popupSize === ModalSize.Medium ? "w-6" : "w-8"
                            )}
                        />
                    </button>
                )}
                {popup.component}
            </div>
        </div>
    );
};

export default Popup;
